
import { AssessmentResponse, RecommendedCareer } from '@/types/assessment';
import { careerCategories } from '@/data/careerCategories';
import { questionData as questions } from '@/data/questions';

// Virtual question IDs for preferred careers
const PREFERRED_CAREER_1_ID = 1001;
const PREFERRED_CAREER_2_ID = 1002;
const PREFERRED_CAREER_3_ID = 1003;

/**
 * Generates career recommendations based on assessment responses
 */
export const generateCareerRecommendations = (responses: AssessmentResponse[]): RecommendedCareer[] => {
  try {
    // Extract user's preferred careers from responses if they exist
    const preferredCareerIds: number[] = [];
    const preferredCareerResponses = responses.filter(r => 
      r.questionId === PREFERRED_CAREER_1_ID || 
      r.questionId === PREFERRED_CAREER_2_ID || 
      r.questionId === PREFERRED_CAREER_3_ID
    );
    
    // Add preferred careers to the list with a high score
    if (preferredCareerResponses.length > 0) {
      preferredCareerResponses.forEach(response => {
        const careerId = parseInt(response.answer);
        if (!isNaN(careerId)) {
          preferredCareerIds.push(careerId);
        }
      });
    }
    
    // This is a simplified approach to generate career recommendations
    // In a real application, you would use a more sophisticated algorithm
    
    // Group responses by category
    const categoryScores: Record<string, { count: number, score: number }> = {};
    
    // Define a mapping of question categories to career categories (simplified example)
    const categoryMapping: Record<string, number[]> = {
      'Realistic': [1, 3, 4, 5, 6, 7],
      'Investigative': [7, 8, 9, 10, 43],
      'Artistic': [14, 16, 17, 36, 42],
      'Social': [11, 15, 31, 32, 33, 37],
      'Enterprising': [19, 20, 21, 22, 23, 24, 25],
      'Conventional': [27, 28, 29, 30, 34]
    };
    
    // Calculate scores for each RIASEC category based on responses
    const regularResponses = responses.filter(r => 
      r.questionId !== PREFERRED_CAREER_1_ID && 
      r.questionId !== PREFERRED_CAREER_2_ID && 
      r.questionId !== PREFERRED_CAREER_3_ID
    );
    
    regularResponses.forEach(response => {
      const question = questions.find(q => q.id === response.questionId);
      if (question) {
        const category = question.category;
        if (!categoryScores[category]) {
          categoryScores[category] = { count: 0, score: 0 };
        }
        
        // Simple scoring: "Yes" = 2, "Maybe" = 1, "No" = 0
        let responseScore = 0;
        if (response.answer === 'Yes' || response.answer === 'yes') responseScore = 2;
        if (response.answer === 'Maybe' || response.answer === 'maybe') responseScore = 1;
        
        categoryScores[category].count++;
        categoryScores[category].score += responseScore;
      }
    });
    
    // Normalize scores to percentages
    const normalizedScores: Record<string, number> = {};
    Object.entries(categoryScores).forEach(([category, data]) => {
      const maxPossibleScore = data.count * 2; // Each question can have max score of 2
      normalizedScores[category] = (data.score / maxPossibleScore) * 100;
    });
    
    // Generate career recommendations based on highest scoring categories
    let recommendations: RecommendedCareer[] = [];
    
    // First, add preferred careers with a high score
    preferredCareerIds.forEach(careerId => {
      const careerCategory = careerCategories.find(c => c.id === careerId);
      if (careerCategory) {
        recommendations.push({
          categoryId: careerCategory.id,
          categoryName: careerCategory.name,
          score: 95 + Math.random() * 5, // High score between 95-100
          subcategories: careerCategory.subcategories.slice(0, 5) // Take up to 5 subcategories
        });
      }
    });
    
    // Then, add recommendations based on assessment
    Object.entries(normalizedScores).forEach(([category, score]) => {
      // Only consider categories with scores above 40%
      if (score > 40) {
        // Get career categories related to this RIASEC category
        const relatedCareerIds = categoryMapping[category] || [];
        
        relatedCareerIds.forEach(careerId => {
          // Skip if already in preferred careers
          if (preferredCareerIds.includes(careerId)) {
            return;
          }
          
          const careerCategory = careerCategories.find(c => c.id === careerId);
          if (careerCategory) {
            // Adjust score slightly for variety
            const adjustedScore = score * (0.85 + Math.random() * 0.3);
            
            // Select a subset of subcategories (up to 5) if there are many
            let selectedSubcategories = [...careerCategory.subcategories];
            if (selectedSubcategories.length > 5) {
              selectedSubcategories = selectedSubcategories
                .sort(() => 0.5 - Math.random())
                .slice(0, 5);
            }
            
            recommendations.push({
              categoryId: careerCategory.id,
              categoryName: careerCategory.name,
              score: adjustedScore,
              subcategories: selectedSubcategories
            });
          }
        });
      }
    });
    
    // Sort by score and take top 10
    return recommendations
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
  } catch (error) {
    console.error('Error generating career recommendations:', error);
    return [];
  }
};

/**
 * Extracts the list of careers selected by the user during assessment
 */
export const extractSelectedCareers = (responses: AssessmentResponse[]): string[] => {
  const selectedCareers: string[] = [];
  const preferredCareerResponses = responses.filter(r => 
    r.questionId === PREFERRED_CAREER_1_ID || 
    r.questionId === PREFERRED_CAREER_2_ID || 
    r.questionId === PREFERRED_CAREER_3_ID
  );
  
  if (preferredCareerResponses.length > 0) {
    for (const response of preferredCareerResponses) {
      const careerId = parseInt(response.answer);
      if (!isNaN(careerId)) {
        const career = careerCategories.find(c => c.id === careerId);
        if (career) {
          selectedCareers.push(career.name);
        }
      }
    }
  }
  
  return selectedCareers;
};
